import {
  Button,
  CircularProgress,
  FormControl,
  InputLabel,
  MenuItem,
  Select,
  Stack,
  ToggleButton,
  ToggleButtonGroup,
  Typography,
} from '@mui/material';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import DownloadIcon from '@mui/icons-material/Download';
import { EXPORT_FORMATS, EXPORT_SIZES } from '../constants';
import type { Dictionary } from '../i18n';
import type { ExportFormat, Ratio } from '../types';
import { SliderField } from './fields';

export interface ExportSettings {
  sizeIndex: number;
  format: ExportFormat;
  quality: number;
}

interface Props {
  t: Dictionary;
  ratio: Ratio;
  settings: ExportSettings;
  onChange: (patch: Partial<ExportSettings>) => void;
  onDownload: () => void;
  onCopy: () => void;
  busy: boolean;
  disabled: boolean;
}

export function ExportControls({
  t,
  ratio,
  settings,
  onChange,
  onDownload,
  onCopy,
  busy,
  disabled,
}: Props) {
  const sizes = EXPORT_SIZES[ratio];
  const sizeIndex = Math.min(settings.sizeIndex, sizes.length - 1);
  const size = sizes[sizeIndex]!;
  const isLossy = EXPORT_FORMATS.find((f) => f.value === settings.format)?.lossy ?? false;

  return (
    <Stack spacing={2}>
      <FormControl fullWidth>
        <InputLabel id="export-size">{t.exportSize}</InputLabel>
        <Select
          labelId="export-size"
          label={t.exportSize}
          value={sizeIndex}
          onChange={(event) => onChange({ sizeIndex: Number(event.target.value) })}
        >
          {sizes.map((option, index) => (
            <MenuItem key={option.label} value={index}>
              {option.label}
            </MenuItem>
          ))}
        </Select>
      </FormControl>

      <ToggleButtonGroup
        exclusive
        fullWidth
        size="small"
        value={settings.format}
        onChange={(_, value: ExportFormat | null) => value !== null && onChange({ format: value })}
        aria-label={t.exportFormat}
      >
        {EXPORT_FORMATS.map((format) => (
          <ToggleButton key={format.value} value={format.value}>
            {format.label}
          </ToggleButton>
        ))}
      </ToggleButtonGroup>

      {isLossy && (
        <SliderField
          label={t.exportQuality}
          unit="%"
          value={settings.quality}
          min={10}
          max={100}
          onChange={(value) => onChange({ quality: value })}
        />
      )}

      <Typography variant="caption" color="text.secondary">
        {size.width} × {size.height} px
      </Typography>

      <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1}>
        <Button
          variant="contained"
          size="large"
          fullWidth
          disabled={disabled || busy}
          startIcon={busy ? <CircularProgress size={18} color="inherit" /> : <DownloadIcon />}
          onClick={onDownload}
        >
          {busy ? t.downloading : t.download}
        </Button>
        <Button
          variant="outlined"
          size="large"
          fullWidth
          disabled={disabled || busy}
          startIcon={<ContentCopyIcon />}
          onClick={onCopy}
        >
          {t.copyToClipboard}
        </Button>
      </Stack>
    </Stack>
  );
}
